// Kopfleiste wie im SRS-Prototyp: Logo, Ansichten, Transport, Zähler mit Tempo/Tonart, Live-Status, Teilnehmer.
import { useLayoutEffect, useRef, useState } from 'react';
import { useStudio, type View } from '../state/store.ts';
import { bbt, bpmAt, hasTempoMap } from '@srs/shared';
import { changeTempoAt } from '../edit/tempoops.ts';
import { run } from '../edit/commands.ts';
import { engine } from '../audio/engine.ts';
import { Icon, SrsMark } from './Icons.tsx';
import { togglePlay, toggleRecord } from './Transport.tsx';
import { useFrame } from './ui.tsx';
import { useBridge } from '../views/Dante.tsx';

const TABS: [View, string][] = [
  ['camp', 'Camp'], ['session', 'Session'], ['compose', 'Compose'], ['klang', 'Klang'], ['mix', 'Mix'],
  ['splits', 'Splits'], ['ar', 'A&R'], ['katalog', 'Katalog'],
];

export function Topbar() {
  const view = useStudio((s) => s.view);
  const setView = useStudio((s) => s.setView);
  const p = useStudio((s) => s.project);
  const peers = useStudio((s) => s.peers);
  const user = useStudio((s) => s.user);
  const online = useStudio((s) => s.online);
  const t = useStudio((s) => s.transport);
  const commit = useStudio((s) => s.commit);
  const bridge = useBridge();
  const mixerOpen = useStudio((s) => s.mixerOpen);
  const pos = useFrame(() => engine.position);
  // Platz: so lange Dinge einklappen, bis die Kopfleiste ganz hineinpasst (unabhängig von Bildschirm, Zoom und Projektnamen)
  const ref = useRef<HTMLElement>(null);
  const [level, setLevel] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      let l = 0;
      const apply = (n: number) => { el.className = `top ${Array.from({ length: n }, (_, i) => `c${i + 1}`).join(' ')}`; };
      apply(0);
      while (l < 6 && el.scrollWidth > el.clientWidth + 1) apply(++l);
      setLevel(l);
    };
    fit();
    const ro = new ResizeObserver(fit); ro.observe(el);
    return () => ro.disconnect();
  }, [p.name, peers.length, online.collab, online.server, bridge.connected]);

  const { bar, beat, tick, num, den } = bbt(p, pos + 1e-7);
  const bpmNow = bpmAt(p, pos), multi = hasTempoMap(p);
  const people = [{ user }, ...peers];

  return (
    <header className={`top ${level ? Array.from({ length: level }, (_, i) => `c${i + 1}`).join(' ') : ''}`} ref={ref}>
      <div className="logo">
        <SrsMark />
        <div><div className="wm">SMART ROYALTY</div><div className="wm-sub"><b>STUDIO</b><span title={p.name}>{p.name}</span></div></div>
      </div>
      <nav className="modes">
        {TABS.map(([v, l]) => <button key={v} className={`mode ${view === v ? 'on' : ''}`} onClick={() => setView(v)}><Icon name={v} />{l}</button>)}
      </nav>
      <div className="trans">
        <button className="rb" title="Zum Anfang (Enter)" onClick={() => engine.locate(0)}><svg viewBox="0 0 16 16"><path d="M2 3h2v10H2zM14 3v10L5 8z" /></svg></button>
        <button className={`rb play ${t.playing ? 'on' : ''}`} title="Wiedergabe (Leertaste)" onClick={() => void togglePlay()}>
          {t.playing ? <svg viewBox="0 0 16 16"><rect x="3" y="3" width="10" height="10" rx="1" /></svg> : <svg viewBox="0 0 16 16"><path d="M3 1.5 14 8 3 14.5z" /></svg>}
        </button>
        <button className={`rb rec ${t.recording ? 'on' : ''}`} title="Aufnahme (R)" onClick={() => void toggleRecord()}><svg viewBox="0 0 16 16"><circle cx="8" cy="8" r="5" /></svg></button>
        <button className={`rb loop ${p.loop.enabled ? 'on' : ''}`} title="Loop (L)" onClick={() => commit('Loop', (x) => { x.loop.enabled = !x.loop.enabled; })}>
          <svg viewBox="0 0 16 16"><path d="M4 5h7l-2-2 1-1 4 3.5L10 9 9 8l2-2H4.5A2.5 2.5 0 0 0 2 8.5V9H.5v-.5A4 4 0 0 1 4 5zm8 6H5l2 2-1 1-4-3.5L6 7l1 1-2 2h6.5A2.5 2.5 0 0 0 14 7.5V7h1.5v.5A4 4 0 0 1 12 11z" /></svg>
        </button>
      </div>
      <div className="clock">
        <div className="main">{bar} | {beat} | {String(tick).padStart(3, '0')}</div>
        <div className="vsep" />
        <label className="sub" title={multi ? 'Tempo an der Abspielposition. Ändern wirkt auf diesen Abschnitt. Alle Wechsel: Doppelklick auf das Tempo-Lineal oder ⌥⇧T' : 'Songtempo. Tempowechsel: Doppelklick auf das Tempo-Lineal oder ⌥⇧T'}>Tempo<TempoInput value={bpmNow} onChange={(v) => commit('Tempo', (x) => changeTempoAt(x, pos, v), { merge: true })} /></label>
        <label className="sub" title="Taktart an der Abspielposition. Ändern: Tempo und Taktart (⌥⇧T)">Takt<button className="meterbtn" onClick={() => run('tempoDialog')}>{num}/{den}</button></label>
        <label className="sub keysub">Tonart<input className="key" value={p.key} onChange={(e) => commit('Tonart', (x) => { x.key = e.target.value; }, { merge: true })} style={{ width: 66 }} /></label>
      </div>
      <UndoRedo />
      <div className="rightbar">
        <span className={`pill status ${online.collab ? 'green' : ''}`} title={online.collab ? `Live, ${people.length} im Raum` : online.server ? 'Server verbunden' : 'Offline'}><i className="ldot" /><span className="stxt">{online.collab ? `Live, ${people.length} im Raum` : online.server ? 'Server verbunden' : 'Offline'}</span></span>
        <button className={`pill mixbtn ${mixerOpen ? 'on' : ''}`} title="Mixer öffnen und schließen (Taste X)" onClick={() => { useStudio.getState().setMixerOpen(!mixerOpen); void engine.ensure(); }}>Mixer <kbd>X</kbd></button>
        <button className={`pill audio ${bridge.connected ? 'cyan' : ''}`} title="Audio-Einrichtung: dein Interface im Browser oder SRS Bridge (Dante)" onClick={() => setView('dante')}>
          <i className="ldot" /><span className="atxt">{bridge.connected ? `Audio: Bridge${bridge.latencyMs !== undefined ? `, ${Math.round(bridge.latencyMs)} ms` : ''}` : 'Audio'}</span>
        </button>
        <div className="avs">
          {people.slice(0, 5).map((x) => <div key={x.user.id} className="av" title={`${x.user.name}${x.user.city ? `, ${x.user.city}` : ''}`} style={{ background: x.user.color }}>{initials(x.user.name)}</div>)}
          {people.length > 5 && <div className="av more">+{people.length - 5}</div>}
        </div>
      </div>
    </header>
  );
}

/** Tempo-Eingabe: tippen ohne dass jeder Zwischenstand (z. B. „1“ beim Tippen von „124“) übernommen wird */
function TempoInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? String(value).replace('.', ',');
  const apply = () => { if (text === null) return; const v = Number(text.replace(',', '.')); if (v >= 20 && v <= 300) onChange(v); setText(null); };
  return <input value={shown} onFocus={(e) => e.target.select()} onChange={(e) => setText(e.target.value)} onBlur={apply} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setText(null); (e.target as HTMLInputElement).blur(); } }} style={{ width: 44 }} />;
}

/** Rückgängig / Wiederholen als Symbole, mit dem Namen des Schritts im Tooltip */
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
function UndoRedo() {
  const h = useStudio((s) => s.history);
  const undoL = h.pastLabels?.[h.pastLabels.length - 1], redoL = h.futureLabels?.[0];
  const mod = isMac ? '⌘' : 'Strg+';
  return (
    <div className="undo">
      <button className="rb" disabled={!h.past.length} title={h.past.length ? `Rückgängig${undoL ? `: ${undoL}` : ''} (${mod}Z)` : 'Nichts rückgängig zu machen'} onClick={() => useStudio.getState().undo()}>
        <svg viewBox="0 0 16 16"><path d="M5.2 3.2 1.5 6.6l3.7 3.4V7.6h4.6a2.9 2.9 0 0 1 0 5.8H6.5V15h3.3a4.5 4.5 0 0 0 0-9H5.2z" /></svg>
      </button>
      <button className="rb" disabled={!h.future.length} title={h.future.length ? `Wiederholen${redoL ? `: ${redoL}` : ''} (${isMac ? '⇧⌘Z' : 'Strg+Umschalt+Z'})` : 'Nichts zu wiederholen'} onClick={() => useStudio.getState().redo()}>
        <svg viewBox="0 0 16 16"><path d="M10.8 3.2l3.7 3.4-3.7 3.4V7.6H6.2a2.9 2.9 0 0 0 0 5.8h3.3V15H6.2a4.5 4.5 0 0 1 0-9h4.6z" /></svg>
      </button>
    </div>
  );
}

export const initials = (n: string) => n.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';
